/**
 * Vorschläge für Gutachter („Angefragt bei“, „Gutachter“) und Institutionen („Anfordern von“).
 * Beide kommen getrennt vom Server – Gutachter nur aus
 * beauftragten, Institutionen nur aus angeforderten Nachweisen –, je mit ihren bisherigen
 * E-Mail-Adressen, die neueste zuerst.
 *
 * Geladen wird jedes Mal, wenn das Feld erscheint – also beim Öffnen des Dialogs bzw. des
 * Nachweises. So steht ein gerade eingetragener Gutachter beim nächsten Nachweis schon in der
 * Liste, ohne dass jemand nach dem Speichern neu laden muss.
 *
 * Ein Fehler bleibt still: Die Vorschläge sind eine Hilfe, das Feld nimmt auch ohne sie Freitext.
 * Externe bekommen vom Server ohnehin keine (403).
 */
import { useEffect, useState } from "react";
import { permitsApi } from "../../../../services/api/permits";
import { EvidenceExpert } from "../../../../types";

/**
 * @param active Ob das Feld gerade zu sehen ist – wechselt es auf `true`, wird neu geladen
 * @param kind Gutachter oder Institutionen
 */
export function useEvidenceExperts(
  active: boolean,
  kind: "experts" | "institutions" = "experts",
): EvidenceExpert[] {
  const [suggestions, setSuggestions] = useState<EvidenceExpert[]>([]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load =
      kind === "experts"
        ? permitsApi.getEvidenceExperts()
        : permitsApi.getEvidenceInstitutions();
    load
      .then((result) => {
        if (!cancelled) setSuggestions(result ?? []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [active, kind]);

  return suggestions;
}

/** Name zum Vergleichen – derselbe Gutachter bis auf Groß-/Kleinschreibung und Leerzeichen */
const sameName = (name: string): string =>
  name.split(/\s+/).filter(Boolean).join(" ").toLowerCase();

/**
 * Die zuletzt verwendete E-Mail-Adresse zu einem Namen. Derselbe Name nach der Regel des Servers
 * (`regeln/experts.py`): gleich bis auf Groß-/Kleinschreibung und Leerzeichen.
 */
export const suggestedEmail = (
  suggestions: EvidenceExpert[],
  name: string,
): string | undefined =>
  suggestions.find((s) => sameName(s.name) === sameName(name))?.emails[0];
